/* RESPONSIBLE TEAM: team-inbox-experience */
import { modifier as buildModifier } from 'ember-modifier';

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const CLICK_ZOOM_SCALE = 2;
const PINCH_ZOOM_SENSITIVITY = 0.01;
const DRAG_THRESHOLD = 2;

interface GestureEvent extends MouseEvent {
  scale: number;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function clampTranslate(
  translateX: number,
  translateY: number,
  scale: number,
  container: HTMLElement,
  image: HTMLImageElement | null,
): { translateX: number; translateY: number } {
  if (!image) {
    return { translateX, translateY };
  }

  let containerRect = container.getBoundingClientRect();
  // offsetWidth/offsetHeight give CSS display size unaffected by transforms
  let baseWidth = image.offsetWidth;
  let baseHeight = image.offsetHeight;

  let overflowX = Math.max(0, (baseWidth * scale - containerRect.width) / 2);
  let overflowY = Math.max(0, (baseHeight * scale - containerRect.height) / 2);

  return {
    translateX: clamp(translateX, -overflowX, overflowX),
    translateY: clamp(translateY, -overflowY, overflowY),
  };
}

function applyTransform(
  element: HTMLElement,
  scale: number,
  translateX: number,
  translateY: number,
) {
  element.style.setProperty('--zoom-scale', String(scale));
  element.style.setProperty('--zoom-tx', String(translateX / scale));
  element.style.setProperty('--zoom-ty', String(translateY / scale));
}

function imageZoom(element: HTMLElement, _positional: unknown[], named: { enabled?: boolean }) {
  if (named.enabled === false) {
    return;
  }
  // Modifier is on the container (needed for bounds clamping) but also needs
  // the child img for cursor styling and click-to-zoom handling.
  let imageEl = element.querySelector('img');
  if (!(imageEl instanceof HTMLImageElement)) {
    return;
  }
  let image: HTMLImageElement = imageEl;

  let scale = MIN_SCALE;
  let translateX = 0;
  let translateY = 0;
  let isPanning = false;
  let isImageLoaded = image.complete && image.naturalWidth > 0;
  let lastPointerX = 0;
  let lastPointerY = 0;
  let didDrag = false;
  let initialGestureScale = MIN_SCALE;
  let activePointers = new Map<number, { x: number; y: number }>();
  let pinchStartDistance: number | null = null;
  let pinchStartScale = MIN_SCALE;

  function isEventWithinContainer(clientX: number, clientY: number) {
    let rect = element.getBoundingClientRect();

    return (
      clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom
    );
  }

  function updateCursor() {
    if (isPanning) {
      image.style.cursor = 'grabbing';
    } else if (scale > MIN_SCALE) {
      image.style.cursor = 'zoom-out';
    } else {
      image.style.cursor = 'zoom-in';
    }
  }

  function setTransition(enabled: boolean) {
    if (enabled) {
      element.removeAttribute('data-panning');
    } else {
      element.setAttribute('data-panning', '');
    }
  }

  // Prevent native image dragging
  image.draggable = false;
  image.style.userSelect = 'none';
  setTransition(true);
  updateCursor();

  function zoomToPoint(
    clientX: number,
    clientY: number,
    nextScaleOrUpdater: number | ((scale: number) => number),
  ) {
    if (!isImageLoaded) {
      return;
    }

    let requestedScale =
      typeof nextScaleOrUpdater === 'function' ? nextScaleOrUpdater(scale) : nextScaleOrUpdater;
    let newScale = clamp(requestedScale, MIN_SCALE, MAX_SCALE);
    if (newScale === scale) {
      return;
    }

    let rect = element.getBoundingClientRect();
    let cursorX = clientX - rect.left - rect.width / 2;
    let cursorY = clientY - rect.top - rect.height / 2;

    let scaleFactor = 1 - newScale / scale;
    let newTranslateX = translateX + cursorX * scaleFactor;
    let newTranslateY = translateY + cursorY * scaleFactor;

    let clamped = clampTranslate(newTranslateX, newTranslateY, newScale, element, image);

    scale = newScale;
    translateX = clamped.translateX;
    translateY = clamped.translateY;

    setTransition(true);
    applyTransform(element, scale, translateX, translateY);
    updateCursor();
  }

  function handleImageLoad() {
    isImageLoaded = true;
  }

  function handleWheel(e: WheelEvent) {
    if (!(e.ctrlKey || e.metaKey) || !isEventWithinContainer(e.clientX, e.clientY)) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    if (!isImageLoaded) {
      return;
    }

    zoomToPoint(e.clientX, e.clientY, (currentScale) => {
      return currentScale + -e.deltaY * PINCH_ZOOM_SENSITIVITY;
    });
  }

  function handleGestureStart(e: Event) {
    let gestureEvent = e as GestureEvent;
    if (!isEventWithinContainer(gestureEvent.clientX, gestureEvent.clientY)) {
      return;
    }

    gestureEvent.preventDefault();
    gestureEvent.stopPropagation();

    if (!isImageLoaded) {
      return;
    }

    initialGestureScale = scale;
  }

  function handleGestureChange(e: Event) {
    let gestureEvent = e as GestureEvent;
    if (!isEventWithinContainer(gestureEvent.clientX, gestureEvent.clientY)) {
      return;
    }

    gestureEvent.preventDefault();
    gestureEvent.stopPropagation();

    if (!isImageLoaded) {
      return;
    }

    zoomToPoint(
      gestureEvent.clientX,
      gestureEvent.clientY,
      initialGestureScale * gestureEvent.scale,
    );
  }

  function handleGestureEnd(e: Event) {
    let gestureEvent = e as GestureEvent;
    if (!isEventWithinContainer(gestureEvent.clientX, gestureEvent.clientY)) {
      return;
    }

    gestureEvent.preventDefault();
    gestureEvent.stopPropagation();
  }

  function handlePointerDown(e: PointerEvent) {
    if (!isImageLoaded) {
      return;
    }

    activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (activePointers.size === 2) {
      let [p1, p2] = [...activePointers.values()];
      pinchStartDistance = Math.hypot(p2.x - p1.x, p2.y - p1.y);
      pinchStartScale = scale;
      isPanning = false;
      setTransition(true);
      updateCursor();
      e.preventDefault();
      return;
    }

    if (scale <= MIN_SCALE || e.button !== 0 || e.target !== image) {
      return;
    }

    e.preventDefault();
    isPanning = true;
    didDrag = false;
    lastPointerX = e.clientX;
    lastPointerY = e.clientY;
    image.setPointerCapture(e.pointerId);
    updateCursor();
  }

  function handlePointerMove(e: PointerEvent) {
    if (!isImageLoaded) {
      return;
    }

    activePointers.set(e.pointerId, { x: e.clientX, y: e.clientY });

    if (activePointers.size === 2 && pinchStartDistance !== null) {
      let [p1, p2] = [...activePointers.values()];
      let currentDistance = Math.hypot(p2.x - p1.x, p2.y - p1.y);
      let midX = (p1.x + p2.x) / 2;
      let midY = (p1.y + p2.y) / 2;
      let newScale = pinchStartScale * (currentDistance / pinchStartDistance);

      zoomToPoint(midX, midY, newScale);
      return;
    }

    if (!isPanning) {
      return;
    }

    let deltaX = e.clientX - lastPointerX;
    let deltaY = e.clientY - lastPointerY;

    if (Math.abs(deltaX) > DRAG_THRESHOLD || Math.abs(deltaY) > DRAG_THRESHOLD) {
      didDrag = true;
    }

    lastPointerX = e.clientX;
    lastPointerY = e.clientY;

    let clamped = clampTranslate(translateX + deltaX, translateY + deltaY, scale, element, image);

    translateX = clamped.translateX;
    translateY = clamped.translateY;

    setTransition(false);
    applyTransform(element, scale, translateX, translateY);
  }

  function handlePointerUp(e: PointerEvent) {
    activePointers.delete(e.pointerId);

    if (pinchStartDistance !== null) {
      pinchStartDistance = null;
      return;
    }

    if (image.hasPointerCapture?.(e.pointerId)) {
      image.releasePointerCapture(e.pointerId);
    }

    isPanning = false;
    setTransition(true);
    updateCursor();
  }

  function handleClick(e: MouseEvent) {
    e.stopPropagation();

    if (didDrag) {
      didDrag = false;
      return;
    }

    if (!isImageLoaded) {
      return;
    }

    if (scale <= MIN_SCALE) {
      zoomToPoint(e.clientX, e.clientY, CLICK_ZOOM_SCALE);
    } else {
      scale = MIN_SCALE;
      translateX = 0;
      translateY = 0;
    }

    setTransition(true);
    applyTransform(element, scale, translateX, translateY);
    updateCursor();
  }

  image.addEventListener('load', handleImageLoad);
  document.addEventListener('wheel', handleWheel, { capture: true, passive: false });
  document.addEventListener('gesturestart', handleGestureStart, { capture: true, passive: false });
  document.addEventListener('gesturechange', handleGestureChange, {
    capture: true,
    passive: false,
  });
  document.addEventListener('gestureend', handleGestureEnd, { capture: true, passive: false });
  element.addEventListener('pointerdown', handlePointerDown);
  element.addEventListener('pointermove', handlePointerMove);
  element.addEventListener('pointerup', handlePointerUp);
  element.addEventListener('pointercancel', handlePointerUp);
  image.addEventListener('pointermove', handlePointerMove);
  image.addEventListener('pointerup', handlePointerUp);
  image.addEventListener('pointercancel', handlePointerUp);
  // Click on image toggles zoom; clicks on container background propagate to close
  image.addEventListener('click', handleClick);

  return function cleanup() {
    image.removeEventListener('load', handleImageLoad);
    document.removeEventListener('wheel', handleWheel, true);
    document.removeEventListener('gesturestart', handleGestureStart, true);
    document.removeEventListener('gesturechange', handleGestureChange, true);
    document.removeEventListener('gestureend', handleGestureEnd, true);
    element.removeEventListener('pointerdown', handlePointerDown);
    element.removeEventListener('pointermove', handlePointerMove);
    element.removeEventListener('pointerup', handlePointerUp);
    element.removeEventListener('pointercancel', handlePointerUp);
    image.removeEventListener('pointermove', handlePointerMove);
    image.removeEventListener('pointerup', handlePointerUp);
    image.removeEventListener('pointercancel', handlePointerUp);
    image.removeEventListener('click', handleClick);
  };
}

interface ImageZoomSignature {
  Element: HTMLElement;
  Args: {
    Named: {
      enabled?: boolean;
    };
  };
}

const modifier = buildModifier<ImageZoomSignature>(imageZoom, { eager: false });

export default modifier;

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'image-zoom': typeof modifier;
  }
}
